import type { PaymentOnboardingStatus } from '@triedge/domain-core';
import type { StripeControlPlaneGuideProps } from '../types';

const readText = (value: unknown, fallback = ''): string => {
  const normalized = String(value ?? '').trim();
  return normalized || fallback;
};

const formatStage = (
  readiness: StripeControlPlaneGuideProps['readiness'],
): { title: string; detail: string } => {
  const status = readiness.status;
  const onboardingStatus = readiness.controlPlane.onboardingStatus;
  const defaultStripeAccountId = readText(readiness.controlPlane.defaultStripeAccountId);

  if (!defaultStripeAccountId) {
    return {
      title: 'Link or create the school Stripe account',
      detail: 'Start with a shared tenant-default connected account before touching manual control-plane fields.',
    };
  }

  if (onboardingStatus !== 'active') {
    return {
      title: 'Finish onboarding and sync Stripe truth',
      detail: 'Complete Stripe Connect in the hosted flow, then synchronize the control plane from Stripe before promoting readiness.',
    };
  }

  if (status === 'not_ready') {
    return {
      title: 'Resolve readiness blockers',
      detail: 'The account is linked, but runtime or Stripe health still has blocking issues. Use the issues list below as the source of truth.',
    };
  }

  return {
    title: status === 'live_ready' ? 'Live-ready' : 'Test-ready',
    detail: 'The tenant-default Stripe control plane is synchronized and ready for hosted payment flows.',
  };
};

const stageTone = (status: StripeControlPlaneGuideProps['readiness']['status']): string => {
  switch (status) {
    case 'live_ready':
      return 'border-[#0F766E] bg-[#ECFDF5] text-[#065F46]';
    case 'test_ready':
      return 'border-[#B45309] bg-[#FFFBEB] text-[#92400E]';
    default:
      return 'border-[#DC2626] bg-[#FEF2F2] text-[#991B1B]';
  }
};

const onboardingOptions: Array<{ value: PaymentOnboardingStatus; label: string }> = [
  { value: 'not_started', label: 'Not started' },
  { value: 'pending', label: 'Pending' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'active', label: 'Active' },
];

export function StripeControlPlaneGuide(props: StripeControlPlaneGuideProps) {
  const stage = formatStage(props.readiness);
  const connectMessage = readText(props.messages?.connectMessage);
  const connectError = readText(props.messages?.connectError);
  const operationMessage = readText(props.messages?.operationMessage);
  const operationError = readText(props.messages?.operationError);
  const payoutsEnabled = props.readiness.stripeAccount.payoutsEnabled;
  const showPayoutNote = payoutsEnabled === false || props.readiness.runtime.stripeSecretMode === 'test';

  return (
    <div className="space-y-4">
      <div className={`rounded-lg border px-4 py-4 ${stageTone(props.readiness.status)}`}>
        <p className="text-xs font-semibold uppercase tracking-[0.16em]">Next Step</p>
        <p className="mt-2 text-sm font-semibold">{stage.title}</p>
        <p className="mt-1 text-sm opacity-90">{stage.detail}</p>
      </div>

      <div className="rounded-lg border border-[#D1D5DB] bg-[#F9FAFB] px-4 py-4 text-sm text-[#374151]">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Readiness Guide
        </p>
        <p className="mt-2">
          <span className="font-semibold">Test-ready</span> means the tenant-default connected account can be used for hosted Stripe flows in test mode.
          <span className="font-semibold"> Live-ready</span> is stricter and requires the intended live secret, live connected account, and payouts enabled.
        </p>
        {showPayoutNote ? (
          <p className="mt-2 rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
            {payoutsEnabled === false
              ? 'Payouts are currently disabled on the connected account. This can remain a staged warning in test mode, but it is a blocking defect for live promotion.'
              : 'Use test mode to finish onboarding and webhook certification first. Live promotion is blocked until the live connected account also reports payouts enabled.'}
          </p>
        ) : null}
      </div>

      <div className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-4">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
          Guided Onboarding
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[#111827]">Contact email</span>
            <input
              value={props.form.contactEmail}
              onChange={(event) => props.onContactEmailChange(event.currentTarget.value)}
              className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
              placeholder="billing@school.org"
              data-testid="stripe-control-plane-contact-email-input"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[#111827]">Country</span>
            <input
              value={props.form.country}
              onChange={(event) => props.onCountryChange(event.currentTarget.value)}
              className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm uppercase text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
              placeholder="US"
              data-testid="stripe-control-plane-country-input"
            />
          </label>
        </div>

        <label className="mt-3 block">
          <span className="mb-1 block text-sm font-medium text-[#111827]">Business type</span>
          <select
            value={props.form.businessType}
            onChange={(event) => props.onBusinessTypeChange(event.currentTarget.value as 'company' | 'individual')}
            className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
            data-testid="stripe-control-plane-business-type-select"
          >
            <option value="company">Company</option>
            <option value="individual">Individual</option>
          </select>
        </label>

        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={props.onStartCreate}
            disabled={Boolean(props.actions?.isStartingOnboarding)}
            className="rounded-md bg-[#1C3B3E] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="stripe-control-plane-create-account-button"
          >
            {props.actions?.isStartingOnboarding ? 'Opening Stripe…' : 'Create Connected Account'}
          </button>
          <button
            type="button"
            onClick={props.onStartLink}
            disabled={Boolean(props.actions?.isStartingOnboarding)}
            className="rounded-md border border-[#D1D5DB] bg-white px-4 py-2 text-sm font-semibold text-[#111827] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="stripe-control-plane-link-account-button"
          >
            Link Existing Account
          </button>
          <button
            type="button"
            onClick={props.onSync}
            disabled={Boolean(props.actions?.isSyncing)}
            className="rounded-md border border-[#D1D5DB] bg-white px-4 py-2 text-sm font-semibold text-[#111827] disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="stripe-control-plane-sync-button"
          >
            {props.actions?.isSyncing ? 'Syncing…' : 'Sync Stripe Status'}
          </button>
        </div>

        {connectMessage ? (
          <p className="mt-3 rounded-md border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-sm text-[#166534]">
            {connectMessage}
          </p>
        ) : null}
        {connectError ? (
          <p className="mt-3 rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#991B1B]">
            {connectError}
          </p>
        ) : null}
      </div>

      <details className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-4">
        <summary className="cursor-pointer text-sm font-semibold text-[#111827]">
          Manual fallback controls
        </summary>
        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[#111827]">Default Stripe account ID</span>
            <input
              value={props.form.defaultStripeAccountId}
              onChange={(event) => props.onDefaultStripeAccountIdChange(event.currentTarget.value)}
              className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
              placeholder="acct_..."
              data-testid="stripe-control-plane-default-account-input"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-[#111827]">Onboarding status</span>
            <select
              value={props.form.onboardingStatus}
              onChange={(event) => props.onOnboardingStatusChange(event.currentTarget.value as PaymentOnboardingStatus)}
              className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
              data-testid="stripe-control-plane-onboarding-status-select"
            >
              {onboardingOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={props.onSave}
            disabled={Boolean(props.actions?.isSaving)}
            className="rounded-md bg-[#1C3B3E] px-4 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            data-testid="stripe-control-plane-save-button"
          >
            {props.actions?.isSaving ? 'Saving…' : 'Save Manual State'}
          </button>
          {operationMessage ? (
            <p className="rounded-md border border-[#BBF7D0] bg-[#F0FDF4] px-3 py-2 text-sm text-[#166534]">
              {operationMessage}
            </p>
          ) : null}
          {operationError ? (
            <p className="rounded-md border border-[#FECACA] bg-[#FEF2F2] px-3 py-2 text-sm text-[#991B1B]">
              {operationError}
            </p>
          ) : null}
        </div>
      </details>
    </div>
  );
}
